import { useConfirm } from "../confirm/ConfirmProvider";
import { useEffect, useMemo, useState } from "react";
import {
  IoAdd,
  IoArrowDown,
  IoArrowUp,
  IoChevronDown,
  IoChevronUp,
} from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import ActionIconButton from "../components/ActionIconButton";
import LoadingOverlay from "../components/Loading";
import ReagentPopup from "../components/ReagentPopup";
import ViewToggle from "../components/ViewToggle";
import { useUser } from "../contexts/UserContext";
import { ENDPOINT } from "../endpoint/endpoint";
import { deleteReagent } from "../fetch/ReagentRequest";
import { reagentFetcher } from "../fetch/SearchRequest";
import {
  reagentDefaultDir,
  ReagentSortField,
  useReagentSort,
} from "../function/useSort";
import { useViewMode } from "../function/useViewMode";
import styles from "../styles/modules/ListPage.module.css";
import { Lab, Reagent } from "../types";

function ReagentListPage() {
  const { groupId } = useParams();
  const { userLabs, memberships, units } = useUser();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const stateLabId = (location.state as { labId?: number } | null)?.labId;

  const [viewMode, setViewMode] = useViewMode();
  const [sort, setSort] = useReagentSort();
  const [storageFilter, setStorageFilter] = useState<string>("all");
  const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
  const [targetReagent, setTargetReagent] = useState<Reagent | null>(null);
  // 廃棄された試薬の折りたたみ表示（既定は閉じる）
  const [showDisposed, setShowDisposed] = useState<boolean>(false);

  const groupIdNum = Number(groupId);

  const selectedLab: Lab | null = useMemo(() => {
    if (userLabs.length === 0) return null;
    return userLabs.find((lab) => lab.id === stateLabId) ?? null;
  }, [userLabs, stateLabId]);

  // 所属研究室が無い場合（大学管理者のみ等）は研究室選択へ、
  // 研究室が特定できない（直接リロード等）場合はマスター画面へ戻す。
  useEffect(() => {
    if (userLabs.length === 0) {
      navigate("/labs", { replace: true });
    } else if (!selectedLab) {
      navigate("/reagents", { replace: true });
    }
  }, [userLabs, selectedLab, navigate]);

  const isLabAdmin = useMemo(
    () =>
      !!selectedLab &&
      memberships.some(
        (m) => m.role_code === "lab_admin" && m.lab_id === selectedLab.id
      ),
    [memberships, selectedLab]
  );

  const {
    data: groupInfos,
    isLoading,
    mutate,
  } = useSWR(
    selectedLab ? ENDPOINT.search.reagentList(selectedLab.id, "") : null,
    reagentFetcher
  );

  const groupInfo = useMemo(
    () =>
      Array.isArray(groupInfos)
        ? groupInfos.find((g) => g.group.id === groupIdNum)
        : undefined,
    [groupInfos, groupIdNum]
  );

  // 保管場所フィルターの選択肢（この試薬に実際に使われている保管場所）
  const storageOptions = useMemo(() => {
    const base = (groupInfo?.reagents ?? []).filter(
      (r) => r.status !== "disposed"
    );
    const map = new Map<number, string>();
    let hasNone = false;
    base.forEach((r) => {
      if (r.storage_location_id != null) {
        map.set(
          r.storage_location_id,
          r.storage_location_name ?? String(r.storage_location_id)
        );
      } else {
        hasNone = true;
      }
    });
    const locations = Array.from(map, ([id, name]) => ({ id, name })).sort(
      (a, b) => a.name.localeCompare(b.name, "ja")
    );
    return { locations, hasNone };
  }, [groupInfo]);

  const reagents = useMemo(() => {
    let list = (groupInfo?.reagents ?? []).filter(
      (r) => r.status !== "disposed"
    );

    // 保管場所で絞り込み
    if (storageFilter === "none") {
      list = list.filter((r) => r.storage_location_id == null);
    } else if (storageFilter !== "all") {
      const id = Number(storageFilter);
      list = list.filter((r) => r.storage_location_id === id);
    }

    // 残量比較のため基準単位に換算する
    const unitMap = new Map(units.map((u) => [u.code, u.to_base_factor]));
    const baseQty = (r: Reagent) =>
      r.quantity_base * (unitMap.get(r.display_unit) ?? 1);

    const dirMul = sort.dir === "asc" ? 1 : -1;
    const byName = (a: Reagent, b: Reagent) =>
      a.name.localeCompare(b.name, "ja");

    return list.slice().sort((a, b) => {
      if (sort.field === "quantity") {
        const q = baseQty(a) - baseQty(b);
        return q !== 0 ? q * dirMul : a.id - b.id;
      }
      if (sort.field === "created") {
        const da = new Date(a.created_at).getTime();
        const db = new Date(b.created_at).getTime();
        return da !== db ? (da - db) * dirMul : a.id - b.id;
      }
      if (sort.field === "updated") {
        const da = new Date(a.updated_at).getTime();
        const db = new Date(b.updated_at).getTime();
        return da !== db ? (da - db) * dirMul : a.id - b.id;
      }
      // name
      const n = byName(a, b);
      return n !== 0 ? n * dirMul : a.id - b.id;
    });
  }, [groupInfo, units, sort, storageFilter]);

  // 廃棄された試薬（削除専用の折りたたみ一覧で表示する）
  const disposedReagents = useMemo(
    () =>
      (groupInfo?.reagents ?? [])
        .filter((r) => r.status === "disposed")
        .sort((a, b) => b.id - a.id),
    [groupInfo]
  );

  function changeSortField(field: ReagentSortField) {
    setSort({ field, dir: reagentDefaultDir(field) });
  }

  function toggleSortDir() {
    setSort({ field: sort.field, dir: sort.dir === "asc" ? "desc" : "asc" });
  }

  function openPopup(reagent: Reagent | null) {
    setTargetReagent(reagent);
    setIsShowPopup(true);
  }

  async function handleDelete(reagent_id: number) {
    if (
      !(await confirm("本当に削除しますか？", {
        danger: true,
        confirmText: "削除",
      }))
    )
      return;
    await deleteReagent({ reagent_id });
    mutate();
  }

  return (
    <>
      <div className={styles["list-page"]}>
        <div className={styles["content"]}>
        <button
          className={styles["back"]}
          onClick={() =>
            navigate("/reagents", { state: { labId: selectedLab?.id } })
          }
        >
          ← マスター一覧
        </button>

        <div className={styles["title-block"]}>
          <h2 className={styles["page-title"]}>試薬一覧</h2>
          {groupInfo && (
            <p className={styles["subtitle"]}>{groupInfo.group.name}</p>
          )}
        </div>

        {(storageOptions.locations.length > 0 || storageOptions.hasNone) && (
          <div className={styles["filter-row"]}>
            <label className={styles["filter-item"]}>
              保管場所で絞り込み
              <select
                className={styles["sort-select"]}
                value={storageFilter}
                onChange={(e) => setStorageFilter(e.target.value)}
              >
                <option value="all">すべて</option>
                {storageOptions.locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
                {storageOptions.hasNone && (
                  <option value="none">未設定</option>
                )}
              </select>
            </label>
          </div>
        )}

        {/* 並び替え（基準 + 昇降）と表示切替 */}
        <div className={styles["toolbar"]}>
          <div className={styles["sort-control"]}>
            <span className={styles["sort-label"]}>並び替え</span>
            <select
              className={styles["sort-select"]}
              value={sort.field}
              onChange={(e) =>
                changeSortField(e.target.value as ReagentSortField)
              }
            >
              <option value="name">名前</option>
              <option value="quantity">残量</option>
              <option value="created">登録日</option>
              <option value="updated">変更日</option>
            </select>
            <button
              type="button"
              className={styles["sort-dir"]}
              onClick={toggleSortDir}
              title={sort.dir === "asc" ? "昇順" : "降順"}
              aria-label={sort.dir === "asc" ? "昇順" : "降順"}
            >
              {sort.dir === "asc" ? <IoArrowUp /> : <IoArrowDown />}
            </button>
          </div>
          <div className={styles["toolbar-actions"]}>
            {isLabAdmin && groupInfo && (
              <button
                type="button"
                className={styles["toolbar-add"]}
                onClick={() => openPopup(null)}
                title="試薬登録"
                aria-label="試薬登録"
              >
                <IoAdd />
              </button>
            )}
            <ViewToggle mode={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {reagents.length === 0 && !isLoading && (
          <p className={styles["empty"]}>試薬がありません。</p>
        )}

        {viewMode === "card" ? (
          <div className={styles["card-grid"]}>
            {reagents.map((reagent) => (
              <div
                key={reagent.id}
                className={styles["card"]}
                onClick={() => navigate(`/reagents/${reagent.id}`)}
              >
                <div className={styles["card-title"]}>{reagent.name}</div>
                <dl className={styles["card-body"]}>
                  <div>
                    <dt>ID</dt>
                    <dd>{reagent.id}</dd>
                  </div>
                  <div>
                    <dt>残量</dt>
                    <dd>
                      {reagent.quantity_base}/{reagent.capacity_base}
                      {reagent.display_unit}
                    </dd>
                  </div>
                  <div>
                    <dt>保管場所</dt>
                    <dd>{reagent.storage_location_name || "-"}</dd>
                  </div>
                  <div>
                    <dt>メーカー</dt>
                    <dd>{reagent.manufacturer || "-"}</dd>
                  </div>
                </dl>
                {isLabAdmin && (
                  <div
                    className={styles["card-actions"]}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionIconButton
                      variant="edit"
                      onClick={() => openPopup(reagent)}
                    />
                    <ActionIconButton
                      variant="delete"
                      onClick={() => handleDelete(reagent.id)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={styles["table-wrap"]}>
            <table className={styles["table"]}>
            <thead>
              <tr>
                <th>ID</th>
                <th>試薬名</th>
                <th>残量</th>
                <th>保管場所</th>
                <th>メーカー</th>
                {isLabAdmin && <th>操作</th>}
              </tr>
            </thead>
            <tbody>
              {reagents.map((reagent) => (
                <tr
                  key={reagent.id}
                  onClick={() => navigate(`/reagents/${reagent.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{reagent.id}</td>
                  <td>{reagent.name}</td>
                  <td>
                    {reagent.quantity_base}/{reagent.capacity_base}
                    {reagent.display_unit}
                  </td>
                  <td>{reagent.storage_location_name || "-"}</td>
                  <td>{reagent.manufacturer || "-"}</td>
                  {isLabAdmin && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionIconButton
                        variant="edit"
                        onClick={() => openPopup(reagent)}
                      />
                      <ActionIconButton
                        variant="delete"
                        onClick={() => handleDelete(reagent.id)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}

        {/* 廃棄された試薬（折りたたみ・削除専用）。既定は閉じる */}
        {disposedReagents.length > 0 && (
          <div className={styles["disposed-section"]}>
            <button
              type="button"
              className={styles["disposed-toggle"]}
              onClick={() => setShowDisposed((v) => !v)}
              aria-expanded={showDisposed}
            >
              {showDisposed ? (
                <IoChevronUp className={styles["disposed-caret"]} />
              ) : (
                <IoChevronDown className={styles["disposed-caret"]} />
              )}
              <span>廃棄された試薬</span>
              <span className={styles["disposed-count"]}>
                {disposedReagents.length}
              </span>
            </button>

            {showDisposed &&
              (viewMode === "card" ? (
                <div className={styles["card-grid"]}>
                  {disposedReagents.map((reagent) => (
                    <div
                      key={reagent.id}
                      className={`${styles["card"]} ${styles["disposed-card"]}`}
                    >
                      <div className={styles["card-title"]}>{reagent.name}</div>
                      <dl className={styles["card-body"]}>
                        <div>
                          <dt>ID</dt>
                          <dd>{reagent.id}</dd>
                        </div>
                        <div>
                          <dt>残量</dt>
                          <dd>
                            {reagent.quantity_base}/{reagent.capacity_base}
                            {reagent.display_unit}
                          </dd>
                        </div>
                      </dl>
                      {isLabAdmin && (
                        <div className={styles["card-actions"]}>
                          <ActionIconButton
                            variant="delete"
                            onClick={() => handleDelete(reagent.id)}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles["table-wrap"]}>
                <table
                  className={`${styles["table"]} ${styles["disposed-table"]}`}
                >
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>試薬名</th>
                      <th>残量</th>
                      <th>保管場所</th>
                      <th>メーカー</th>
                      {isLabAdmin && <th>削除</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {disposedReagents.map((reagent) => (
                      <tr key={reagent.id} className={styles["disposed-row"]}>
                        <td>{reagent.id}</td>
                        <td>{reagent.name}</td>
                        <td>
                          {reagent.quantity_base}/{reagent.capacity_base}
                          {reagent.display_unit}
                        </td>
                        <td>{reagent.storage_location_name || "-"}</td>
                        <td>{reagent.manufacturer || "-"}</td>
                        {isLabAdmin && (
                          <td>
                            <ActionIconButton
                              variant="delete"
                              onClick={() => handleDelete(reagent.id)}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              ))}
          </div>
        )}
        </div>
      </div>

      {isShowPopup && groupInfo && (
        <ReagentPopup
          targetReagent={targetReagent}
          selectedLab={selectedLab}
          units={units}
          targetReagentGroup={groupInfo.group}
          onClose={() => setIsShowPopup(false)}
          onLoading={() => {}}
          updateReagentList={() => mutate()}
        />
      )}

      {isLoading && <LoadingOverlay />}
    </>
  );
}

export default ReagentListPage;
